package com.nonopn.skywatcher.common.app.pages.map.view.views

import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.padding
import androidx.compose.material.ripple
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.nonopn.skywatcher.common.app.api.impl.WarnStateValidationState
import com.nonopn.skywatcher.common.app.home.LocalThunderDetectionModel
import com.nonopn.skywatcher.common.app.pages.map.model.MainScreenModel
import com.nonopn.skywatcher.common.app.pages.map.model.weather.AlertType
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.alert_confirm_dialog_text
import com.nonopn.skywatcher.common.app.res.alert_confirm_dialog_title
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_button_orange
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_button_red
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_button_yellow
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_orange_text
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_orange_title
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_red_text
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_red_title
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_text
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_title
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_yellow_text
import com.nonopn.skywatcher.common.app.res.alert_user_dialog_yellow_title
import com.nonopn.skywatcher.common.app.res.cancel
import com.nonopn.skywatcher.common.app.res.warn_customer_text_client
import com.nonopn.skywatcher.common.app.res.warn_customer_text_manager
import com.nonopn.skywatcher.design.local.LocalFontSizes
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.localized
import com.nonopn.skywatcher.design.widgets.DefaultCard
import com.nonopn.skywatcher.design.widgets.PopupConfirm
import com.nonopn.skywatcher.design.widgets.PopupConfirmAction
import com.nonopn.skywatcher.design.widgets.PopupConfirmWithButtonRows
import eu.codlab.compose.widgets.TextNormal
import korlibs.io.util.UUID


@Composable
fun ShowAlertButton(
    modifier: Modifier,
    model: MainScreenModel
) {
    val thunderDetection = LocalThunderDetectionModel.current
    val thunderDetectionState by thunderDetection.states.collectAsState()
    val state by model.states.collectAsState()

    if (thunderDetectionState.selectedThunderDetection.isAdministrator) {
        ShowManualAlertButton(modifier, model)
    } else {
        ShowConfirmAlertButton(modifier, model)
    }
}

private val mapOfAlerts = mapOf(
    AlertType.ManualYellow to (Res.string.alert_user_dialog_yellow_title to Res.string.alert_user_dialog_yellow_text),
    AlertType.ManualOrange to (Res.string.alert_user_dialog_orange_title to Res.string.alert_user_dialog_orange_text),
    AlertType.ManualRed to (Res.string.alert_user_dialog_red_title to Res.string.alert_user_dialog_red_text),
)

@Composable
fun ShowManualAlertButton(
    modifier: Modifier,
    model: MainScreenModel
) {
    var showConfirmationWarnUsers: String? by remember { mutableStateOf(null) }
    var showFinalConfirmationToUsers: String? by remember { mutableStateOf(null) }
    var selectedAlertType: AlertType? by remember { mutableStateOf(null) }

    val localSelectedAlertType = selectedAlertType
    if (null != localSelectedAlertType) {
        PopupConfirm(
            shownUuid = showFinalConfirmationToUsers,
            title = mapOfAlerts[localSelectedAlertType]!!.first.localized(),
            text = mapOfAlerts[localSelectedAlertType]!!.second.localized(),
            showCancel = true,
            onDismiss = {
                showFinalConfirmationToUsers = null
                showConfirmationWarnUsers = null
            },
            onConfirm = {
                model.sendManualAlert(localSelectedAlertType)
                showFinalConfirmationToUsers = null
                showConfirmationWarnUsers = null
            }
        )
    }

    PopupConfirmWithButtonRows(
        shownUuid = showConfirmationWarnUsers,
        title = Res.string.alert_user_dialog_title.localized(),
        text = Res.string.alert_user_dialog_text.localized(),
        onDismiss = {
            showConfirmationWarnUsers = null
        },
        rowsOfActions = listOf(
            listOf(
                Triple(
                    AlertType.ManualYellow,
                    Res.string.alert_user_dialog_button_yellow,
                    AppColor.YellowDark
                ),
                Triple(
                    AlertType.ManualOrange,
                    Res.string.alert_user_dialog_button_orange,
                    AppColor.Orange
                ),
                Triple(
                    AlertType.ManualRed,
                    Res.string.alert_user_dialog_button_red,
                    AppColor.RedDark
                )
            ).map { (alert, text, backgroundColor) ->
                PopupConfirmAction(
                    text = text.localized(),
                    textColor = AppColor.WhiteCream,
                    backgroundColor = backgroundColor,
                    isOutlined = true,
                    onClick = {
                        selectedAlertType = alert
                        showFinalConfirmationToUsers = UUID.randomUUID().toString()
                        showConfirmationWarnUsers = null
                    }
                )
            },
            listOf(
                PopupConfirmAction(
                    text = Res.string.cancel.localized(),
                    isOutlined = false,
                    onClick = {
                        showConfirmationWarnUsers = null
                    }
                )
            )
        )
    )

    Column(
        modifier.padding(12.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        DefaultCard(
            Modifier,
            backgroundColor = AppColor.RedDark,
            columnModifier = Modifier
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = ripple(),
                ) {
                    showConfirmationWarnUsers = UUID.randomUUID().toString()
                }.padding(12.dp)
        ) {
            TextNormal(
                text = Res.string.warn_customer_text_manager.localized(),
                fontSize = LocalFontSizes.current.map.alertBannerSub,
                textAlign = TextAlign.Center,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
fun ShowConfirmAlertButton(
    modifier: Modifier,
    model: MainScreenModel
) {
    val state by model.states.collectAsState()
    var showConfirmationWarnUsers: String? by remember { mutableStateOf(null) }

    val needConfirmation =
        state.usersOnMap.current?.alertState == WarnStateValidationState.NeedsConfirm

    if (!needConfirmation) {
        return
    }

    PopupConfirm(
        shownUuid = showConfirmationWarnUsers,
        title = Res.string.alert_confirm_dialog_title.localized(),
        text = Res.string.alert_confirm_dialog_text.localized(),
        showCancel = true,
        onConfirm = {
            model.confirmAlertSeen()
            showConfirmationWarnUsers = null
        },
        onDismiss = {
            showConfirmationWarnUsers = null
        },
    )

    Column(
        modifier,
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        DefaultCard(
            Modifier,
            backgroundColor = AppColor.RedDark,
            columnModifier = Modifier
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = ripple(),
                ) {
                    showConfirmationWarnUsers = UUID.randomUUID().toString()
                }.padding(12.dp)
        ) {
            TextNormal(
                text = Res.string.warn_customer_text_client.localized(),
                fontSize = LocalFontSizes.current.map.alertBannerSub,
                textAlign = TextAlign.Center,
                fontWeight = FontWeight.Bold
            )
        }
    }
}